import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

/**
 * FeedbackPrompt
 * Bottom slide-up micro-survey for CheckoutDM merchants.
 *
 * USAGE (in Dashboard.jsx):
 *   import FeedbackPrompt from "@/components/FeedbackPrompt";
 *   ...
 *   <FeedbackPrompt merchant={me} />
 *
 * Mount it once near DashboardNotifications — it decides on its own
 * whether to show, based on the cooldown rules below.
 *
 * COOLDOWN STATE (server-side): read from merchant.feedback_last_answered_at
 * and merchant.feedback_last_snoozed_at on the businesses row — NOT
 * localStorage. This makes the timer tied to the merchant account, not
 * the browser/device, so switching between desktop/mobile or adding
 * iOS/Android apps later doesn't reset it. GET /api/me must include
 * both fields for this to work — if they're missing/undefined here,
 * daysSince() below correctly reads that as "never", which is safe
 * (worst case: prompt shows when it shouldn't have to yet) but won't
 * respect a real cooldown until that endpoint returns them.
 *
 * BACKEND (n8n):
 *   Submit: Webhook -> Verify JWT -> insert into merchant_feedback,
 *   then in parallel stamp businesses.feedback_last_answered_at.
 *   Dismiss: Webhook -> Verify JWT -> stamp businesses.feedback_last_snoozed_at.
 */

const FEEDBACK_ENDPOINT = "/merchant/feedback";
const FEEDBACK_DISMISS_ENDPOINT = "/merchant/feedback/dismiss";

const COOLDOWN_DAYS = 30; // don't re-ask someone who already answered
const SNOOZE_DAYS = 7; // if dismissed without answering, wait a week
const MIN_ACCOUNT_AGE_DAYS = 3; // don't ask brand-new signups on day one

function daysSince(timestamp) {
  if (!timestamp) return Infinity;
  return (Date.now() - timestamp) / (1000 * 60 * 60 * 24);
}

export default function FeedbackPrompt({ merchant }) {
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const [step, setStep] = useState("rate"); // 'rate' | 'comment' | 'done'
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!merchant?.id) return;

    if (daysSince(merchant.feedback_last_answered_at ? new Date(merchant.feedback_last_answered_at).getTime() : null) < COOLDOWN_DAYS) return;
    if (daysSince(merchant.feedback_last_snoozed_at ? new Date(merchant.feedback_last_snoozed_at).getTime() : null) < SNOOZE_DAYS) return;

    if (merchant.created_at) {
      const age = daysSince(new Date(merchant.created_at).getTime());
      if (age < MIN_ACCOUNT_AGE_DAYS) return;
    }

    const timer = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(timer);
  }, [merchant]);

  const dismiss = useCallback((persist = true) => {
    setClosing(true);
    setTimeout(() => setVisible(false), 250);
    if (persist && merchant?.id) {
      // Best-effort — a failed snooze write just means the prompt might
      // reappear a bit sooner than 7 days on this merchant's next visit.
      // Not worth blocking the close animation on.
      api.post(FEEDBACK_DISMISS_ENDPOINT, { business_id: merchant.id }).catch(() => {});
    }
  }, [merchant]);

  const handleRate = (value) => {
    setRating(value);
    setStep("comment");
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await api.post(FEEDBACK_ENDPOINT, {
        business_id: merchant.id,
        rating,
        comment: comment.trim() || null,
        page_context: window.location.pathname,
      });
      setStep("done");
      setTimeout(() => dismiss(false), 1800);
    } catch (e) {
      setError("Couldn't send that — check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible || !merchant?.id) return null;

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-4 transition-transform duration-300 ease-out ${
        closing ? "translate-y-full" : "translate-y-0"
      }`}
      style={{ animation: closing ? undefined : "cdm-feedback-slide-up 0.3s ease-out" }}
      data-testid="feedback-prompt"
    >
      <style>{`
        @keyframes cdm-feedback-slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>

      <div className="w-full max-w-md rounded-[1.75rem] border border-ink/10 bg-white p-5 shadow-lg shadow-ink/10">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            {step === "rate" && (
              <>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-hotpink">Quick check-in</p>
                <p className="mt-1 font-display text-lg font-black tracking-tight text-ink">
                  How's CheckoutDM working for you?
                </p>
                <div className="mt-3 flex gap-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      onClick={() => handleRate(n)}
                      data-testid={`feedback-rate-${n}`}
                      className="h-9 w-9 rounded-full border border-ink/15 text-sm font-bold text-ink hover:bg-ink hover:text-cream transition-colors"
                      aria-label={`Rate ${n} out of 5`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <div className="mt-1.5 flex justify-between text-[10px] font-semibold uppercase tracking-wide text-ink/40">
                  <span>Not great</span>
                  <span>Love it</span>
                </div>
              </>
            )}

            {step === "comment" && (
              <>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-hotpink">One more thing</p>
                <p className="mt-1 font-display text-lg font-black tracking-tight text-ink">
                  Anything specific we should know?
                </p>
                <textarea
                  autoFocus
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Optional — a bug, a request, anything."
                  rows={3}
                  data-testid="feedback-comment-input"
                  className="mt-2 w-full resize-none rounded-2xl border border-ink/15 p-2.5 text-sm text-ink placeholder:text-ink/35 focus:border-hotpink focus:outline-none"
                />
                {error && <p className="mt-1.5 text-xs font-semibold text-hotpink">{error}</p>}
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    data-testid="feedback-submit"
                    className="cta-glow rounded-full bg-sunset-gradient px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {submitting ? "Sending…" : "Send feedback"}
                  </button>
                  <button
                    onClick={() => dismiss(true)}
                    className="rounded-full px-3.5 py-1.5 text-sm font-semibold text-ink/50 hover:text-ink transition-colors"
                  >
                    Skip
                  </button>
                </div>
              </>
            )}

            {step === "done" && (
              <p className="font-display text-lg font-black tracking-tight text-ink">Thanks — that helps. 🙌</p>
            )}
          </div>

          {step !== "done" && (
            <button
              onClick={() => dismiss(true)}
              aria-label="Close feedback prompt"
              className="text-ink/25 hover:text-ink/60 transition-colors text-lg leading-none"
            >
              ×
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
